import { memo, useEffect, useMemo, useRef, useState, type Ref } from 'react'
import { Info, Loader2, Pencil } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { SHOT_LIST_COLUMN_DEFINITIONS, SHOT_LIST_FRAMING_PRESETS, type ShotFrameLinkDict, type ShotListRowDict, type ShotVideoPackDict } from '../../../../generated/nodeDataTypes'
import { createNadDebug } from '../../../../utils/debugLog'
import { cameraDescriptionIsRedundant, openingTransition, outgoingTransition } from './shotListPresentation'
import { ShotEditError, durationSecondsFromMs, validateShotEdit, type ShotEditField } from './shotListEdits'

const shotListLog = createNadDebug('dw:shot-list:transition')

export type FramingField = 'shot' | 'angle' | 'lens' | 'focus'
export type ShotListColumnId = (typeof SHOT_LIST_COLUMN_DEFINITIONS)[number]['id']

export type ShotFrameRole = ShotFrameLinkDict['role']
export type ShotFrameTarget = Pick<ShotFrameLinkDict, 'shot_id' | 'role' | 'node_id'>
export type ShotListEmptyState = 'empty' | 'queued' | 'running' | 'awaiting_cost'
type FrameColumnDefinition = Extract<(typeof SHOT_LIST_COLUMN_DEFINITIONS)[number], { role: ShotFrameRole }>
export const SHOT_LIST_FRAME_COLUMNS: readonly FrameColumnDefinition[] = SHOT_LIST_COLUMN_DEFINITIONS.filter(
  (column): column is FrameColumnDefinition => 'role' in column,
)

/** Append a typed descriptor here when a new row field (e.g. keyframe description) ships. */
export const SHOT_LIST_COLUMNS: readonly { id: ShotListColumnId; width: string }[] = SHOT_LIST_COLUMN_DEFINITIONS.filter(
  column => !('role' in column),
)

export const FRAMING_FIELDS: readonly FramingField[] = ['shot', 'angle', 'lens', 'focus']
const FRAMING_OPTIONS: Readonly<Record<FramingField, readonly string[]>> = SHOT_LIST_FRAMING_PRESETS

/** A selected image owns the preview; never substitute an unrelated generated frame. */
export function resolveShotFramePreview(
  selected: { preview?: string } | undefined,
  link: ShotFrameLinkDict | undefined,
  framePreview: ((link: ShotFrameLinkDict) => string | undefined) | undefined,
): string | undefined {
  return selected ? selected.preview : link?.status === 'ready' ? framePreview?.(link) : undefined
}

export function formatShotTimecode(milliseconds: number): string {
  if (!Number.isSafeInteger(milliseconds) || milliseconds < 0) {
    throw new Error(`Invalid shot-list timecode: ${milliseconds}`)
  }
  const hours = Math.floor(milliseconds / 3_600_000)
  const minutes = Math.floor(milliseconds / 60_000) % 60
  const seconds = Math.floor(milliseconds / 1000) % 60
  const fraction = milliseconds % 1000
  const clock = [hours, minutes, seconds].map(n => String(n).padStart(2, '0')).join(':')
  return fraction ? `${clock}.${String(fraction).padStart(3, '0')}` : clock
}

export type ShotListTableProps = {
  rows: readonly ShotListRowDict[]
  sourceKind?: 'flat' | 'guided' | 'screenplay' | 'video'
  disabled?: boolean
  onFramingChange?: (rowId: string, field: FramingField, value: string) => void
  onCellChange?: (rowId: string, field: ShotEditField, value: string, expected?: string) => boolean | void
  onOpeningTransitionChange?: (value: string, boundary: 'Hard' | 'Soft', expected?: string) => boolean | void
  openingSeamTransition?: string
  onFrameRelink?: (rowId: string, role: ShotFrameRole, imageNodeId: string) => boolean | void
  frameChoices?: readonly { id: string; label: string }[]
  duplicateFrameKeys?: readonly string[]
  onFramePickerActiveChange?: (active: boolean) => void
  selectedFrames?: Readonly<Record<string, { imageNodeId: string; preview?: string; origin: 'user' | 'director' }>>
  locationParts?: Readonly<Record<string, { interior_exterior: string; location: string; time_of_day: string }>>
  boundaryTypes?: Readonly<Record<string, 'Hard' | 'Soft' | 'Continuous'>>
  frameColumns?: readonly ShotFrameRole[]
  frameLinks?: readonly ShotFrameLinkDict[]
  videoPacks?: readonly ShotVideoPackDict[]
  framePreview?: (link: ShotFrameLinkDict) => string | undefined
  onFrameFocus?: (link: ShotFrameTarget) => void
  scrollRef?: Ref<HTMLDivElement>
  logInvalidBoundaries?: boolean
  emptyState?: ShotListEmptyState
  announceEmptyState?: boolean
}

function EditableCell({ value, expectedValue, label, disabled, multiline, onCommit, children }: {
  value: string; expectedValue?: string; label: string; disabled?: boolean; multiline?: boolean
  onCommit?: (value: string, expected: string) => boolean | void; children?: React.ReactNode
}) {
  const { t } = useTranslation('canvas')
  const [draft, setDraft] = useState<string | null>(null)
  const [error, setError] = useState('')
  const cancelled = useRef(false)
  const originalValue = useRef(value)
  const commit = () => {
    if (cancelled.current) { cancelled.current = false; return }
    if (draft === null) return
    if (draft !== value) {
      try { if (onCommit?.(draft, originalValue.current) === false) return; setError('') } catch (reason) { setError(reason instanceof ShotEditError ? t(reason.key, reason.params) : String(reason)); return }
    }
    setDraft(null)
  }
  return <div className="group relative min-w-0">
    {draft === null ? <div className="flex items-start gap-1">
      <div className="min-w-0 flex-1">{children ?? <PlainCell>{value}</PlainCell>}</div>
      {onCommit && <button type="button" aria-label={t('shotList.editField', { field: label })} title={t('shotList.editField', { field: label })}
        disabled={disabled} onClick={() => { cancelled.current = false; originalValue.current = expectedValue ?? value; setDraft(value); setError('') }}
        className="nodrag shrink-0 rounded-lg border border-input-border bg-surface-1 p-1 text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50">
        <Pencil aria-hidden="true" className="h-3 w-3" />
      </button>}
    </div> : <div className="space-y-1">
      {multiline ? <textarea autoFocus aria-label={label} rows={3} value={draft} onChange={event => setDraft(event.currentTarget.value)}
        onBlur={commit} onKeyDown={event => { if (event.nativeEvent.isComposing || event.keyCode === 229) return; if (event.key === 'Escape') { /* SHORTCUT_ID: shotList.cancelCell */ cancelled.current = true; setDraft(null) } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) { /* SHORTCUT_ID: shotList.commitCell */ event.preventDefault(); commit() } }}
        className="nodrag w-full resize-y rounded-lg border border-input-border bg-input-bg p-2 text-foreground hover:border-input-border-hover hover:bg-surface-3 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
        : <input autoFocus aria-label={label} value={draft} onChange={event => setDraft(event.currentTarget.value)}
          onBlur={commit} onKeyDown={event => { if (event.nativeEvent.isComposing || event.keyCode === 229) return; if (event.key === 'Escape') { /* SHORTCUT_ID: shotList.cancelCell */ cancelled.current = true; setDraft(null) } else if (event.key === 'Enter') { /* SHORTCUT_ID: shotList.commitCell */ event.preventDefault(); commit() } }}
          className="nodrag w-full rounded-lg border border-input-border bg-input-bg p-2 text-foreground hover:border-input-border-hover hover:bg-surface-3 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />}
    </div>}
    {error && <span role="alert" className="block text-xs text-destructive">{error}</span>}
  </div>
}

function PlainCell({ children }: { children?: string }) {
  const { t } = useTranslation('canvas')
  return children ? <span className="whitespace-pre-wrap break-words">{children}</span> : <span className="text-text-muted" aria-label={t('shotList.unspecified', { defaultValue: 'Not specified' })}>—</span>
}

export function ShotVisualDescription({ row }: { row: ShotListRowDict }) {
  const { t } = useTranslation('canvas')
  const camera = row.camera_movement?.trim()
  return <div className="space-y-1">
    <PlainCell>{row.visual_description}</PlainCell>
    {camera && !cameraDescriptionIsRedundant(row.visual_description, camera) &&
      <div className="whitespace-pre-wrap break-words"><span className="font-semibold text-foreground/80">{t('shotList.cameraLabel')}: </span>{camera}</div>}
  </div>
}

export function ShotOutgoingTransition({ row, next }: { row: ShotListRowDict; next?: ShotListRowDict }) {
  const { t } = useTranslation('canvas')
  const transition = outgoingTransition(row, next)
  if (transition.kind === 'hard-cut') return <span className="sr-only">{t('shotList.hardCut')}</span>
  return <span title={transition.uncut ? t('shotList.continuousHelp') : undefined}>
    {transition.kind === 'continuous' ? t('shotList.continuous') : transition.label}
    {transition.uncut && <span className="block text-xs text-muted-foreground">{t('shotList.continuousHelp')}</span>}
  </span>
}

function FramingControl({ rowId, number, field, value, disabled, onFramingChange }: {
  rowId: string
  number: string
  field: FramingField
  value: string
  disabled?: boolean
  onFramingChange?: ShotListTableProps['onFramingChange']
}) {
  const { t } = useTranslation('canvas')
  const [isCustom, setIsCustom] = useState(false)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState('')
  const cancelled = useRef(false)
  const choices = FRAMING_OPTIONS[field]
  const commitCustom = () => {
    if (cancelled.current) {
      cancelled.current = false
      return
    }
    const trimmed = draft.trim()
    try {
      if (trimmed) onFramingChange?.(rowId, field, trimmed)
      setError('')
    } catch (reason) {
      setError(reason instanceof ShotEditError ? t(reason.key, reason.params) : String(reason))
      return
    }
    setIsCustom(false)
  }
  const accessibleName = t('shotList.framingControl', { number, field: t(`shotList.framing.${field}`), defaultValue: `Shot ${number} ${field}` })
  return (
    <label className="flex items-center gap-2">
      <span className="w-11 shrink-0 font-semibold text-foreground/80">
        {t(`shotList.framing.${field}`, { defaultValue: field[0].toUpperCase() + field.slice(1) })}
      </span>
      {isCustom ? <input
        autoFocus
        aria-label={accessibleName}
        className="min-w-0 w-full rounded-lg border border-input-border bg-input-bg px-1 text-xs text-foreground hover:border-input-border-hover hover:bg-surface-3 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        value={draft}
        disabled={disabled}
        onChange={event => setDraft(event.currentTarget.value)}
        onBlur={commitCustom}
        onKeyDown={event => {
          if (event.nativeEvent.isComposing || event.keyCode === 229) return
          if (event.key === 'Enter') { /* SHORTCUT_ID: shotList.commitCell */ event.preventDefault(); commitCustom() }
          if (event.key === 'Escape') { /* SHORTCUT_ID: shotList.cancelCell */ event.preventDefault(); cancelled.current = true; setIsCustom(false) }
        }}
      /> : <select
        aria-label={accessibleName}
        className="min-w-0 w-full rounded-lg border border-input-border bg-input-bg px-1 text-xs text-foreground hover:bg-surface-3 hover:border-input-border-hover focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        value={value}
        disabled={disabled || !onFramingChange}
        onChange={event => {
          if (event.currentTarget.value === '__custom__') {
            cancelled.current = false
            setDraft(value && !choices.includes(value) ? value : '')
            setError('')
            setIsCustom(true)
          } else {
            try {
              onFramingChange?.(rowId, field, event.currentTarget.value)
              setError('')
            } catch (reason) { setError(reason instanceof ShotEditError ? t(reason.key, reason.params) : String(reason)) }
          }
        }}
      >
        <option value="">{t('shotList.unspecified', { defaultValue: 'Not specified' })}</option>
        {value && !choices.includes(value) && <option value={value}>{value}</option>}
        {choices.map(option => <option value={option} key={option}>{option}</option>)}
        <option value="__custom__">{t('shotList.custom', { defaultValue: 'Custom…' })}</option>
      </select>}
      {error && <span role="alert" className="text-xs text-destructive">{error}</span>}
    </label>
  )
}

function FramingCell({ row, number, disabled, onFramingChange }: {
  row: ShotListRowDict
  number: string
  disabled?: boolean
  onFramingChange?: ShotListTableProps['onFramingChange']
}) {
  return (
    <div className="space-y-1.5">
      {FRAMING_FIELDS.map(field => {
        return <FramingControl key={field} rowId={row.id} number={number} field={field}
          value={row[`framing_${field}`] ?? ''} disabled={disabled} onFramingChange={onFramingChange} />
      })}
    </div>
  )
}

export const ShotListTable = memo(function ShotListTable({ rows, sourceKind = 'flat', disabled, onFramingChange, onCellChange, onOpeningTransitionChange, openingSeamTransition, onFrameRelink, frameChoices = [], duplicateFrameKeys = [], onFramePickerActiveChange, selectedFrames = {}, locationParts = {}, boundaryTypes = {}, frameColumns = [], frameLinks = [], videoPacks = [], framePreview, onFrameFocus, scrollRef, logInvalidBoundaries = true, emptyState, announceEmptyState = true }: ShotListTableProps) {
  const { t } = useTranslation('canvas')
  const fieldLabel = (number: string, field: string) => t('shotList.shotField', { number, field: t(`shotList.editFields.${field}`) })
  const [actionError, setActionError] = useState('')
  const commitFrame = (shotId: string, role: ShotFrameRole, nodeId: string) => {
    try {
      onFrameRelink?.(shotId, role, nodeId)
      setActionError('')
    } catch (error) { setActionError(error instanceof ShotEditError ? t(error.key, error.params) : String(error)) }
  }
  const commitBoundary = (shotId: string, value: string, expected: string) => {
    try {
      if (onCellChange?.(shotId, 'boundary_type', value, expected) === false) {
        shotListLog({ event: 'boundary-edit-rejected', shotId })
        setActionError(t('shotList.errors.boundaryNotSaved'))
        return
      }
      setActionError('')
    } catch (error) { setActionError(error instanceof ShotEditError ? t(error.key, error.params) : String(error)) }
  }
  const columns = useMemo(() => [
    ...SHOT_LIST_COLUMNS,
    ...(videoPacks.length ? [{ id: 'video_generation_id', width: 'w-28' }] : []),
    ...SHOT_LIST_FRAME_COLUMNS.filter(column => frameColumns.includes(column.role)),
  ], [frameColumns, videoPacks.length])
  const packByShot = useMemo(() => {
    const result = new Map<string, ShotVideoPackDict>()
    let cursor = 0
    for (const pack of videoPacks) {
      if (!pack.shot_ids.length || !pack.video_node_id || !Number.isSafeInteger(pack.generation_id)) {
        throw new Error(`Invalid Shot List video generation pack: ${pack.id}`)
      }
      for (const id of pack.shot_ids) {
        if (rows[cursor]?.id !== id || result.has(id)) {
          throw new Error(`Shot List generation ${pack.generation_id} is out of order or stale at ${id}`)
        }
        result.set(id, pack)
        cursor++
      }
    }
    if (videoPacks.length && cursor !== rows.length) throw new Error('Shot List video generations do not cover every shot')
    return result
  }, [rows, videoPacks])
  const links = useMemo(() => {
    const result = new Map<string, ShotFrameLinkDict>()
    for (const link of frameLinks) {
      const key = `${link.shot_id}:${link.role}`
      if (result.has(key)) throw new Error(`Duplicate Shot List frame link ${key}`)
      result.set(key, link)
    }
    return result
  }, [frameLinks])
  const timings = useMemo(() => {
    const ids = new Set<string>()
    let elapsed = 0
    return rows.map(row => {
      if (!row.id || ids.has(row.id)) throw new Error(`Duplicate or missing shot-list row ID: ${row.id}`)
      if (!Number.isSafeInteger(row.duration_ms) || row.duration_ms <= 0) {
        throw new Error(`Invalid duration_ms on shot-list row ${row.id}`)
      }
      ids.add(row.id)
      const start = elapsed
      elapsed += row.duration_ms
      return { start, end: elapsed }
    })
  }, [rows])
  const invalidBoundaries = rows.map((row, index) => outgoingTransition(row, rows[index + 1]).invalidBoundary ? row.id : '').filter(Boolean).join(',')
  useEffect(() => {
    if (logInvalidBoundaries && invalidBoundaries) shotListLog({ event: 'continuous-invalid-boundary', shotIds: invalidBoundaries })
  }, [invalidBoundaries, logInvalidBoundaries])

  const sequenceStart = openingTransition(rows)

  if (rows.length === 0) {
    const busy = emptyState === 'queued' || emptyState === 'running'
    const message = emptyState === 'running' ? t('shotList.loading')
      : emptyState === 'queued' ? t('shotList.queued')
      : emptyState === 'awaiting_cost' ? t('storyboardDesignerNode.waitingForCostApproval')
      : t('shotList.empty', { defaultValue: 'No shots in this list yet' })
    return <div ref={scrollRef} tabIndex={scrollRef ? 0 : undefined}
      role={emptyState !== undefined && announceEmptyState ? 'status' : undefined}
      aria-live={emptyState !== undefined && announceEmptyState ? 'polite' : undefined}
      className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20" data-testid="shot-list-empty">
      {busy && <Loader2 aria-hidden="true" className={`h-5 w-5 ${emptyState === 'queued' ? 'motion-safe:animate-spin-slow' : 'motion-safe:animate-spin'}`} />}
      <span>{message}</span>
    </div>
  }

  return (
    <div ref={scrollRef} tabIndex={scrollRef ? 0 : undefined} className="h-full overflow-auto nodrag nowheel nokey focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20" data-canvas-local-keys="" data-testid="shot-list-scroll" onWheel={e => e.stopPropagation()}>
      {actionError && <p role="alert" className="border-b border-border px-3 py-2 text-xs text-destructive">{actionError}</p>}
      {(sequenceStart || onOpeningTransitionChange) && <div className="border border-border bg-surface-0 px-3 py-2 text-xs text-muted-foreground" data-testid="shot-list-sequence-start">
        {t('shotList.sequenceStart')}: <EditableCell label={t('shotList.editFields.openingTransition')} value={sequenceStart ?? ''} expectedValue={openingSeamTransition} disabled={disabled}
          onCommit={onOpeningTransitionChange ? (value, expected) => onOpeningTransitionChange(value, value.trim() && !['cut', 'cut to', 'hard cut'].includes(value.trim().toLowerCase()) ? 'Soft' : 'Hard', expected) : undefined} />
      </div>}
      <table className="w-full min-w-max table-fixed border-collapse text-left text-sm text-muted-foreground" aria-label={t('shotList.tableLabel', { defaultValue: 'Shot List' })}>
        <colgroup>{columns.map(column => <col className={column.width} key={column.id} />)}</colgroup>
        <thead className="sticky top-0 z-10 bg-surface-2 text-foreground/75">
          <tr>{columns.map(column => (
            <th scope="col" key={column.id} className="border border-border px-2 py-3 font-semibold">
              <span className="flex items-center justify-between gap-1">
                {column.id === 'video_generation_id'
                  ? t('shotList.columns.video_generation_id', { defaultValue: 'Generation ID' })
                  : t(`shotList.columns.${column.id}`)}
                {column.id === 'framing' && <span title={t('shotList.framingHelp', { defaultValue: 'Shot size, angle, lens, and focus' })} tabIndex={0} aria-label={t('shotList.framingHelp', { defaultValue: 'Shot size, angle, lens, and focus' })}><Info className="h-4 w-4" /></span>}
              </span>
            </th>
          ))}</tr>
        </thead>
        <tbody className="bg-surface-1">
          {rows.map((row, index) => {
            const number = row.shot_number || String(index + 1).padStart(2, '0')
            const time = timings[index]
            return <tr key={row.id} className="align-top" data-testid={`shot-list-row-${row.id}`}>
              {columns.map(column => {
                let content: React.ReactNode
                switch (column.id) {
                  case 'video_generation_id': {
                    const pack = packByShot.get(row.id)
                    if (!pack) throw new Error(`Shot ${row.id} has no video Generation ID`)
                    if (pack.shot_ids[0] !== row.id) return null
                    return <td key={column.id} rowSpan={pack.shot_ids.length}
                      title={t('shotList.videoNode', { id: pack.video_node_id })}
                      className="border border-border bg-surface-2 px-2 py-3 align-middle text-center tabular-nums text-foreground"
                      data-testid={`shot-generation-${pack.generation_id}`}>
                      <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-lg border border-input-border bg-surface-1 px-2 text-xs font-semibold">
                        {pack.generation_id}
                      </span>
                      <span className="mt-1 block text-xxs text-muted-foreground">
                        {t('shotList.generationShotCount', {
                          count: pack.shot_ids.length,
                          defaultValue: pack.shot_ids.length === 1 ? '{{count}} shot' : '{{count}} shots',
                        })}
                      </span>
                    </td>
                  }
                  case 'start_frame':
                  case 'key_frame':
                  case 'end_frame': {
                    const role = SHOT_LIST_FRAME_COLUMNS.find(item => item.id === column.id)!.role
                    const link = links.get(`${row.id}:${role}`)
                    const selected = selectedFrames[`${row.id}:${role}`]
                    const preview = resolveShotFramePreview(selected, link, framePreview)
                    const label = t(`shotList.columns.${column.id}`)
                    const effectiveLink: ShotFrameTarget | undefined = selected ? { node_id: selected.imageNodeId, shot_id: row.id, role } : link
                    content = <div className="space-y-1"><button type="button"
                      disabled={!effectiveLink || !onFrameFocus || !preview}
                      onClick={() => effectiveLink && onFrameFocus?.(effectiveLink)}
                      aria-label={t('shotList.openFrame', { number, role: label, defaultValue: `Open shot ${number} ${label}` })}
                      className="nodrag inline-flex w-full flex-col gap-1 rounded-lg border border-input-border bg-input-bg p-1 text-left text-xs text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-60">
                      {preview && <img src={preview} alt="" className="h-20 w-full rounded-lg object-contain" />}
                      <span>{selected ? `${t(selected.origin === 'user' ? 'shotList.relinked' : 'shotList.directorAnchor')} · ${t(preview ? 'shotList.ready' : 'shotList.unavailable')}`
                        : link ? link.status === 'ready' && !preview ? t('shotList.frameUnavailable', { defaultValue: 'Image node unavailable' }) : t(`shotList.frameStatus.${link.status}`, { defaultValue: link.status }) : t('shotList.noFrameYet')}</span>
                    </button>{onFrameRelink && <select aria-label={t('shotList.relinkFrame', { number, role: label })} value={selected?.origin === 'user' ? selected.imageNodeId : ''}
                      disabled={disabled} onFocus={() => onFramePickerActiveChange?.(true)}
                      onBlur={() => onFramePickerActiveChange?.(false)}
                      onChange={event => commitFrame(row.id, role, event.currentTarget.value)}
                      className="nodrag w-full rounded-lg border border-input-border bg-input-bg px-1 py-1 text-xs text-foreground hover:border-input-border-hover hover:bg-surface-3 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50">
                      <option value="">{t(selected?.origin === 'director' ? 'shotList.replaceDirectorAnchor' : 'shotList.useGeneratedFrame')}</option>
                      {selected?.origin === 'user' && !frameChoices.some(choice => choice.id === selected.imageNodeId) &&
                        <option value={selected.imageNodeId}>{selected.preview ? selected.imageNodeId : t('shotList.selectedImageUnavailable', { defaultValue: 'Selected image unavailable — choose another or clear' })}</option>}
                      {frameChoices.map(choice => <option key={choice.id} value={choice.id}>{choice.label}</option>)}
                    </select>}
                    {duplicateFrameKeys.includes(`${row.id}:${role}`) && onFrameRelink &&
                      <button type="button" disabled={disabled} onClick={() => commitFrame(row.id, role, selected?.imageNodeId ?? '')}
                        className="nodrag w-full rounded-lg border border-input-border bg-surface-1 p-1 text-xs text-warning hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20">
                        {t('shotList.resolveFrameConflict', { defaultValue: 'Conflicting frame edits — keep this image' })}
                      </button>}
                    </div>
                    break
                  }
                  case 'shot_number': content = <span className="font-semibold tabular-nums">{number}</span>; break
                  case 'timecode': content = <div className="space-y-1 tabular-nums">
                    <div><strong className="inline-block w-16">{t('shotList.in', { defaultValue: 'In' })}</strong>{formatShotTimecode(time.start)}</div>
                    <div><strong className="inline-block w-16">{t('shotList.out', { defaultValue: 'Out' })}</strong>{formatShotTimecode(time.end)}</div>
                    <EditableCell label={fieldLabel(number, 'durationSeconds')} value={durationSecondsFromMs(row.duration_ms)} disabled={disabled}
                      onCommit={onCellChange && sourceKind !== 'video' ? (draft, expected) => onCellChange(row.id, 'duration_ms', draft, validateShotEdit(sourceKind, 'duration_ms', expected, false)) : undefined}>
                      <div><strong className="inline-block w-16">{t('shotList.duration', { defaultValue: 'Duration' })}</strong>{row.duration_ms / 1000}s</div>
                    </EditableCell>
                  </div>; break
                  case 'visual_description': content = <div className="space-y-1"><EditableCell label={fieldLabel(number, 'description')} value={row.visual_description} multiline disabled={disabled} onCommit={onCellChange ? (draft, expected) => onCellChange(row.id, 'visual_description', draft, expected) : undefined} />
                    <EditableCell label={fieldLabel(number, 'cameraMovement')} value={row.camera_movement ?? ''} disabled={disabled} onCommit={onCellChange ? (draft, expected) => onCellChange(row.id, 'camera_movement', draft, expected) : undefined} /></div>; break
                  case 'location': content = sourceKind === 'flat'
                    ? <EditableCell label={fieldLabel(number, 'location')} value={row.location ?? ''} disabled={disabled} onCommit={onCellChange ? (draft, expected) => onCellChange(row.id, 'location', draft, expected) : undefined} />
                    : <div className="space-y-1">
                      <EditableCell label={fieldLabel(number, 'interiorExterior')} value={locationParts[row.id]?.interior_exterior ?? ''} disabled={disabled} onCommit={onCellChange ? (draft, expected) => onCellChange(row.id, 'interior_exterior', draft, expected) : undefined} />
                      <EditableCell label={fieldLabel(number, 'location')} value={locationParts[row.id]?.location ?? ''} disabled={disabled} onCommit={onCellChange ? (draft, expected) => onCellChange(row.id, 'location', draft, expected) : undefined} />
                      <EditableCell label={fieldLabel(number, 'timeOfDay')} value={locationParts[row.id]?.time_of_day ?? ''} disabled={disabled} onCommit={onCellChange ? (draft, expected) => onCellChange(row.id, 'time_of_day', draft, expected) : undefined} />
                    </div>; break
                  case 'framing': content = <FramingCell row={row} number={number} disabled={disabled} onFramingChange={onFramingChange} />; break
                  case 'transition': content = <div className="space-y-1"><EditableCell label={fieldLabel(number, 'outgoingTransition')} value={row.transition_out ?? ''} disabled={disabled}
                    onCommit={onCellChange ? (draft, expected) => onCellChange(row.id, 'transition', draft, expected) : undefined}><ShotOutgoingTransition row={row} next={rows[index + 1]} /></EditableCell>
                    {onCellChange && sourceKind !== 'flat' && <select aria-label={fieldLabel(number, 'boundary')} disabled={disabled} value={boundaryTypes[row.id] ?? (outgoingTransition(row, rows[index + 1]).kind === 'continuous' ? 'Continuous' : outgoingTransition(row, rows[index + 1]).kind === 'hard-cut' ? 'Hard' : 'Soft')}
                      onChange={event => commitBoundary(row.id, event.currentTarget.value, boundaryTypes[row.id] ?? '')}
                      className="nodrag w-full rounded-lg border border-input-border bg-input-bg px-1 py-1 text-xs text-foreground hover:border-input-border-hover hover:bg-surface-3 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"><option value="Hard">{t('shotList.boundaryHard', { defaultValue: 'Hard' })}</option><option value="Soft">{t('shotList.boundarySoft', { defaultValue: 'Soft' })}</option><option value="Continuous">{t('shotList.continuous')}</option></select>}</div>; break
                  case 'dialogue_voiceover': content = <EditableCell label={fieldLabel(number, 'dialogue')} value={row.dialogue_voiceover ?? ''} disabled={disabled} multiline onCommit={onCellChange ? (draft, expected) => onCellChange(row.id, 'dialogue_voiceover', draft, expected) : undefined} />; break
                  case 'sfx': content = <EditableCell label={fieldLabel(number, 'soundEffects')} value={row.sfx ?? ''} disabled={disabled} multiline onCommit={onCellChange ? (draft, expected) => onCellChange(row.id, 'sfx', draft, expected) : undefined} />; break
                }
                const cellClass = 'border border-border px-2 py-3 leading-relaxed'
                return column.id === 'shot_number'
                  ? <th scope="row" key={column.id} className={cellClass}>{content}</th>
                  : <td key={column.id} className={cellClass}>{content}</td>
              })}
            </tr>
          })}
        </tbody>
      </table>
    </div>
  )
})
